import { useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import {
  AlignLeft, BookOpen, Image as ImageIcon, Languages, Music, SquarePen, X,
} from 'lucide-react'
import { useLocale } from '../i18n/context'

interface Props {
  /** `label` is only passed for the custom type — the label the user typed
   * before content, per brief §11 ("Custom asks for its label before its
   * content"). Undefined for the other five, which have a fixed label. */
  onSelect: (kind: string, label?: string) => void
  onClose: () => void
}

/**
 * Compact inline type chooser for + Field (brief §11, §16 Constraints). Not a
 * modal and not a bottom sheet — a small popover the caller (DeckMobileFeed)
 * anchors above the editing bar, same fixed slot the field-undo/save-error
 * strip uses.
 *
 * **It offers the six types brief §11 enumerates**, and only those:
 * Translation, Definition, Example, Image, Audio, Custom. That list is a
 * closed enumeration, not a snapshot of the backend taxonomy — see ADR-067's
 * eighth amendment (Decision E), which is what this component is now measured
 * against. It once offered four (Image and Audio "deliberately absent"), which
 * became wrong the moment the retired full-screen editor's sheet went onto a
 * retirement path: the phone was left with no way to create an image or audio
 * field at all, and "replace an image or audio file" became a capability with
 * no way to reach its own subject. Fixing that was right. Mirroring
 * `SIDE_B_KINDS` wholesale in the same pass was not — it added `dialog`,
 * `note` and `link`, which §11 does not list, and they are gone again. A kind
 * existing in storage is not an argument for a pill: those kinds are still
 * stored, still rendered and still editable wherever a card already carries
 * one, and `Custom` is the way to author anything the five named types do not
 * cover. Adding a seventh pill here is an ADR amendment, not a reconciliation.
 *
 * Both surfaces are served by this one component — the phone's editing bar and
 * the desktop right panel's `+ Field` — so the two cannot drift again.
 *
 * **Layout is a phone question, not a list question.** This popover sits in a
 * fixed slot above the editing bar with the keyboard raised, so the free space
 * it lives in is roughly 350px, not the viewport. The pills are therefore
 * compact (30px tall, 11px of horizontal padding, 12.5px label) and ordered so
 * the natural wrap points fall between GROUPS rather than mid-group — text
 * kinds, then attachments, then Custom. At 370px of usable width (390px
 * device, less the caller's 10px insets) six of them settle into two rows,
 * inside the three the nine-type list needed, so the card underneath keeps the
 * majority of the band. Nothing here scrolls — ADR-067 Decision 3's
 * prohibition is about scrollports, and a chooser that needed one would be a
 * chooser that had outgrown its slot.
 *
 * Repeating an existing type is allowed (ADR-066 — insertion always appends,
 * so a second Example can never become authoritative), so there is no
 * "already used" disabled state here, unlike the retired editor's sheet.
 *
 * **Keyboard-completable (brief §22 desktop pass 2).** Reached from the
 * desktop card's `⌘⇧F` with the caret still in whatever field the author was
 * typing in — not from a click on the trigger button — so this has to take
 * focus itself rather than assume a click already put it somewhere useful.
 * Two properties live here:
 *  - Opens with focus already on its first option (`autoFocus` below), so the
 *    reader's very next keypress (⇥ or Enter) is already inside it.
 *  - `Escape` closes it from anywhere inside — the type buttons, the close
 *    button, or the custom-label input — without hunting for the × with a
 *    mouse.
 * The third property brief §22 asks for — dismissal returns focus where it
 * came from — is NOT here. Capturing "where it came from" has to happen
 * BEFORE this component exists: React applies `autoFocus` during the same
 * commit that mounts this component, ahead of any effect this component
 * could run, so an effect here reading `document.activeElement` on mount
 * would already be reading the pill `autoFocus` just moved focus TO, not the
 * field the reader was in a moment earlier. The caller (`DeckDesktopPanels`)
 * captures it at the point it decides to open this popover — the last moment
 * "before" is still knowable — and restores it after this unmounts. The
 * caller also has to be the one to skip that restore when a type was picked
 * (the reader's real next stop is the new field), which needs the same
 * "before this existed" vantage point this component does not have.
 */
type CardEditorLabels = ReturnType<typeof useLocale>['tr']['cardEditor']
interface FieldTypeOption {
  kind: string
  Icon: typeof Languages
  label: (ce: CardEditorLabels) => string
}

// The two named groups, in render order, followed by Custom. Split so the
// popover's natural wrap falls between them rather than through one — see the
// layout note in the class doc. Together with Custom these are exactly brief
// §11's six types; the list is closed (ADR-067 amendment 8, Decision E).
const TEXT_KINDS: FieldTypeOption[] = [
  { kind: 'translation', Icon: Languages, label: (ce) => ce.translationLabel },
  { kind: 'definition', Icon: BookOpen, label: (ce) => ce.definitionLabel },
  { kind: 'example', Icon: AlignLeft, label: (ce) => ce.exampleLabel },
]
const ATTACHMENT_KINDS: FieldTypeOption[] = [
  { kind: 'image', Icon: ImageIcon, label: (ce) => ce.imageLabel },
  { kind: 'audio', Icon: Music, label: (ce) => ce.audioLabel },
]

export default function FieldTypeChooser({ onSelect, onClose }: Props) {
  const { tr } = useLocale()
  const fc = tr.fieldChooser
  const ce = tr.cardEditor

  // null = choosing a type; a string = the custom-label step, entered before
  // any content field appears (brief §11).
  const [customLabel, setCustomLabel] = useState<string | null>(null)

  function handleKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Escape') return
    e.preventDefault()
    // This popover is a DOM sibling of the card, not a descendant of it (both
    // callers render it alongside the card, never inside it), so bubbling
    // could not reach the card's own `Escape` handler either way — stopped
    // anyway so a future caller that DOES nest the two does not inherit a
    // double-close for free.
    e.stopPropagation()
    onClose()
  }

  // Custom is stored as a note-kind side carrying the author's own label —
  // unchanged, and deliberately not widened to stand in for the plain Note
  // pill this chooser no longer offers (ADR-067 amendment 8). This is now the
  // ONLY path that creates a note-kind side, and every note-kind side IS a
  // Custom field (`isCustomNamedSide` — kind alone decides, not the word
  // typed here, so naming this field "Note" is as valid as naming it anything
  // else and does not resurrect the retired plain-Note treatment).
  //
  // The typed name is passed through verbatim (trimmed) — never coerced to
  // `undefined`, which is what let the caller substitute a default name for it.
  // A blank name yields no field at all: the caller discards it the same way an
  // empty field is discarded, so nothing is created and nothing is renamed.
  const confirmCustom = () => onSelect('note', (customLabel ?? '').trim())

  return (
    <div style={wrapStyle} onKeyDown={handleKeyDown}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {customLabel === null ? (
          <>
            <span style={titleStyle}>{fc.title}</span>
            <div style={{ flex: 1 }} />
          </>
        ) : (
          <input
            autoFocus
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value.slice(0, 50))}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmCustom() }}
            placeholder={ce.customFieldNamePlaceholder}
            style={labelInputStyle}
          />
        )}
        <button onClick={onClose} aria-label={fc.closeAria} style={closeBtnStyle}>
          <X size={14} strokeWidth={2.2} />
        </button>
      </div>

      {customLabel === null ? (
        <div style={pillRowStyle}>
          {/* Grouped in the order described above — text, attachments, then
              Custom — so a wrap falls between groups. `autoFocus` stays on the first
              pill: the keyboard cycle's ⌘⇧F lands here and the reader's next
              key must already be inside. */}
          {TEXT_KINDS.map((t, i) => (
            <button
              key={t.kind}
              autoFocus={i === 0}
              onClick={() => onSelect(t.kind)}
              aria-label={fc.addFieldAria(t.label(ce))}
              style={pillStyle}
            >
              <t.Icon size={13} strokeWidth={2} />
              {t.label(ce)}
            </button>
          ))}
          {ATTACHMENT_KINDS.map((t) => (
            <button
              key={t.kind}
              onClick={() => onSelect(t.kind)}
              aria-label={fc.addFieldAria(t.label(ce))}
              style={pillStyle}
            >
              <t.Icon size={13} strokeWidth={2} />
              {t.label(ce)}
            </button>
          ))}
          <button onClick={() => setCustomLabel('')} aria-label={fc.customAria} style={pillStyle}>
            <SquarePen size={13} strokeWidth={2} />
            {ce.customFieldLabel}
          </button>
        </div>
      ) : (
        <button onClick={confirmCustom} style={confirmBtnStyle}>
          {fc.addBtn}
        </button>
      )}
    </div>
  )
}

const wrapStyle: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 9,
  background: 'var(--bg-surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
  padding: '10px 12px',
}

const titleStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 700,
  color: 'var(--text-secondary)',
}

const closeBtnStyle: CSSProperties = {
  flexShrink: 0, width: 26, height: 26, padding: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)',
}

// Wraps rather than forcing a single unbreakable row — the one-row
// requirement in the brief is about the pinned editing bar itself, not this
// floating popover; a second line here never grows the bar or steals a
// second scrolling surface.
const pillRowStyle: CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 8,
}

// Compact by measurement, not by taste: these share a popover living in the
// ~350px above a raised keyboard. See the class doc's layout note for the row
// arithmetic this height and padding were chosen against — sized when the list
// was nine long, and left as it is now that it is six: the pill is not the
// place to spend the reclaimed row.
const pillStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  height: 30, padding: '0 11px',
  background: 'var(--bg-page)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-pill)', color: 'var(--text-primary)',
  fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap',
}

const labelInputStyle: CSSProperties = {
  flex: 1, minWidth: 0, height: 36, boxSizing: 'border-box',
  fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)',
  background: 'var(--bg-page)', border: '1.5px solid var(--accent)',
  borderRadius: 8, padding: '0 11px', outline: 'none',
}

const confirmBtnStyle: CSSProperties = {
  alignSelf: 'flex-end',
  height: 36, padding: '0 16px',
  background: 'var(--control-solid)', color: 'var(--control-solid-text)',
  border: 'none', borderRadius: 'var(--radius-pill)', cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
}
